import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { selectAccessorySizes } from '@ff-scene-plugins/photo-prints-redux/selectors/sizeSelectors';
import { createUiBindings } from '@ff-scene-plugins/photo-prints-redux/slices/generalUiSlice';
import { PhotoPrintsPdpState } from '@ff-scene-plugins/photo-prints-redux/store';
import { selectHasFotofunBorder } from '../../_redux-borders';
import { AccessoryGrid, AccessoryItem } from './accessories';
import { FotofunInPanelBanner, ViewAllButton, magic_effects, standard_effects, FotoFunTitle, convertAccessorySizesToUIFormat } from './components';
import { DraggableImageSquare, GridContainer, PriceLabelFrames } from './frames';
import styles from './FotofunPanel.module.scss';

type MainContentProps = {
    setPanelContentTab: (tab: 'main' | 'magical' | 'standard' | 'accessories') => void;
};

const MainContent = ({ setPanelContentTab }: MainContentProps) => {

    // This variabel is used to control the visibility of the accessories section.
    // It can be set to false if you want to hide the accessories section.
    const showAccessories = true;

    const { t } = useTranslation();

    const dispatch = useDispatch();
    const hasFotofunBorder = useSelector(selectHasFotofunBorder);
    const accessorySizes = useSelector((state: PhotoPrintsPdpState) => selectAccessorySizes(state));
    const accessories = convertAccessorySizesToUIFormat(accessorySizes);

    const { createUpdateAction } = createUiBindings();

    const setCanHaveBorderDraggedInto = createUpdateAction('canHaveBorderDraggedInto');

    const handleDragStart = () => {
        dispatch(setCanHaveBorderDraggedInto(true));
    };

    const handleDragEnd = () => {
        dispatch(setCanHaveBorderDraggedInto(false));
    };

    return (
        <div className={`${styles.mainContent}`}>

            {hasFotofunBorder && <FotofunInPanelBanner />}

            <div className="p-6">
                <div className={styles.titlePriceLabelWrapper}>
                    <h4 className={styles.title}>
                        {t('photo_prints.frames')}
                    </h4>
                    <PriceLabelFrames  />
                </div>

                <p className={styles.subtitle}>{t('photo_prints.magic_frame_text_2')}</p>

                <GridContainer className={`${styles.effectContainer} mb-3`}>
                    {magic_effects.slice(0,11).map((effect, index) => (
                        <DraggableImageSquare
                            key={effect.id}
                            effect={effect}
                            onDragStart={handleDragStart}
                            onDragEnd={handleDragEnd}
                            className={`${styles.effectSquare} ${styles['effectMagic' + index]}`}
                        />
                    ))}
                    {standard_effects.slice(0,10).map((effect, index) => (
                        <DraggableImageSquare
                            key={effect.id}
                            effect={effect}
                            onDragStart={handleDragStart}
                            onDragEnd={handleDragEnd}
                            className={`${styles.effectSquare} ${styles['effectStandard' + index]}`}
                        />
                    ))}
                    <ViewAllButton
                        onClick={() => setPanelContentTab('magical')}
                        className={styles.effectButton}
                    />
                </GridContainer>


                {showAccessories && (
                    <>
                        <h4 className={styles.title}>{t('photo_prints.accessories_title')}</h4>

                        <p className={styles.subtitle}>{t('photo_prints.accessory_subtitle')}</p>

                        <AccessoryGrid className={`${styles.accessoryContainer}`}>
                            {accessories.slice(0,5).map((accessory, index) => (
                                <AccessoryItem
                                    key={accessory.id}
                                    accessory={accessory}
                                    className={`${styles.accessorySquare} ${styles['effectAccessory' + index]}`} />
                            ))}
                            <ViewAllButton
                                onClick={() => setPanelContentTab('accessories')}
                                className={styles.accessoryButton}
                            />
                        </AccessoryGrid>
                    </>
                )}
            </div>
        </div>
    );
};

export { MainContent };